import Vue, { VNode } from 'vue'
import { throttle, Cancelable } from 'lodash'

import { cloneDeep, defaultsDeep, isEqual, isObject, merge, transform } from 'lodash'

export interface Data {
  updatesWhileEditing: object[]
  updatesWhileSaving: object[]
  editCopy: object
  commonBase: object
  makingCopy: boolean
  saving: boolean
  throttledSave: Cancelable & (() => any)
}

export default Vue.extend({
  computed: {
    clean(): boolean {
      return !this.differsFromBase(this.source)
    },
    modified(): boolean {
      return this.differsFromBase(this.editCopy)
    },
    changes(): object {
      return this.difference(this.editCopy, this.commonBase)
    },
    differences(): object {
      return this.difference(this.source, this.commonBase)
    },
    stale(): boolean {
      return this.updatesWhileEditing.length > 0
    },
    source(): object {
      return this.value
    },
    slotProperties(): object {
      return {
        changes: this.changes,
        clean: this.clean,
        conflicts: this.updatesWhileEditing,
        copy: this.editCopy,
        differences: this.differences,
        emit: this.emit,
        modified: this.modified,
        reset: this.reset,
        save: this.save,
        saving: this.saving,
        stale: this.stale,
      }
    },
  },
  created() {
    this.$emit('update:copy', this.editCopy)
    if (this.saveOn) {
      this.$on(this.saveOn, () => this.save())
    }
  },
  data(): Data {
    return {
      commonBase: cloneDeep(this.value),
      editCopy: defaultsDeep(cloneDeep(this.value), this.defaults),
      makingCopy: false,
      saving: false,
      throttledSave: throttle(
        (update: object = {}) => {
          ;(this as any).save(update)
        },
        2000,
        { leading: false }
      ),
      updatesWhileEditing: [],
      updatesWhileSaving: [],
    }
  },
  beforeDestroy() {
    this.throttledSave.cancel()
  },
  inject: {
    injectedElement: {
      default: undefined,
      from: 'element',
    },
    injectedSaveMethod: {
      default: undefined,
      from: 'save',
    },
  },
  methods: {
    differsFromBase(other: object): boolean {
      return !isEqual(this.commonBase, other)
    },
    clearConflicts() {
      this.updatesWhileEditing = []
      this.updatesWhileSaving = []
    },
    fetchCopy(fromObject?: object) {
      const sourceClone = fromObject ? cloneDeep(fromObject) : cloneDeep(this.source)
      this.commonBase = sourceClone
      this.editCopy = defaultsDeep(cloneDeep(sourceClone), this.defaults)
    },
    reset() {
      this.commonBase = cloneDeep(this.value)
      this.editCopy = defaultsDeep(cloneDeep(this.value), this.defaults)
      this.makingCopy = false
      this.saving = false
      this.updatesWhileEditing = []
      this.updatesWhileSaving = []
    },
    async save(update: object = {}): Promise<object> {
      if (this.saving) {
        throw new Error('already saving at the moment')
      }

      this.saving = true
      merge(this.editCopy, update)
      try {
        const savedState = await Promise.resolve(this.saveWith(this.editCopy, this.changes))
        const clone = cloneDeep(savedState)
        this.fetchCopy(clone)
        this.$emit('change', clone)
        this.$emit('saved', clone)
        this.saving = false
        return savedState
      } catch (error) {
        this.saving = false
        throw error
      }
    },
    emit(event: string = 'change') {
      this.$emit(event, cloneDeep(this.editCopy))
    },
    /**
 * https://gist.github.com/Yimiprod/7ee176597fef230d1451
 *
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
  difference(object, base) {
    return transform(object, (result, value, key) => {
      if (!isEqual(value, base[key])) {
        result[key] = isObject(value) && isObject(base[key]) ? this.difference(value, base[key]) : value
      }
    })
}
  },
  props: {
    autosave: {
      default: false,
      type: Boolean,
    },
    copy: {
      type: Object,
    },
    defaults: {
      default: () => ({}),
      type: Object,
    },
    saveOn: {
      type: String,
    },
    saveWith: {
      default() {
        return (this as any).injectedSaveMethod || ((x: any) => x)
      },
      type: [Object, Function],
    },
    tag: {
      default: 'div',
      type: String,
    },
    value: {
      default() {
        console.log((this as any).injectedElement)
        return (this as any).injectedElement || {}
      },
      type: Object,
    },
  },
  render(): VNode {
    const Tag = this.tag
    return (
      <Tag {...this.$attrs}>
        {this.$scopedSlots.default ? this.$scopedSlots.default(this.slotProperties) : ''}
      </Tag>
    )
  },
  watch: {
    changes(nowIs: object) {
      this.$emit('update:changes', cloneDeep(nowIs))
    },
    modified(nowIs: boolean, was: boolean) {
      if (this.autosave) {
        console.log('modified, autosave!')
        this.throttledSave()
      }
    },
    copy(nowIs: object, was: object) {
      const isNewAssignment = nowIs !== was
      if (isNewAssignment) {
        this.editCopy = nowIs
      }
    },
    editCopy: {
      deep: true,
      handler(nowIs: object, was: object) {
        const isNewAssignment = nowIs !== was
        if (!isNewAssignment) {
          this.$emit('input', cloneDeep(nowIs) /* by value */)
        }
        this.$emit('update:copy', nowIs /* by reference */)
      },
    },
    saving(nowIs: boolean, was: boolean): void {
      const saved = was === true && nowIs === false
      if (saved) {
        this.updatesWhileSaving
          .filter(update => this.differsFromBase(update))
          .forEach(update => this.updatesWhileEditing.push(update))
        this.updatesWhileSaving = []
      }
    },
    source: {
      deep: true,
      handler(nowIs: object, was: object) {
        const sourceClone = cloneDeep(this.source)

        // stash incoming changes, when the copy has been edited.
        if (this.modified) {
          if (this.saving) {
            this.updatesWhileSaving.push(sourceClone)
          } else {
            this.updatesWhileEditing.push(sourceClone)
          }
        }

        // reset editor for incoming changes, if the copy is still clean
        if (!this.modified && this.differsFromBase(sourceClone)) {
          this.fetchCopy(sourceClone)
          this.clearConflicts()
        }
      },
    },
  },
})
